import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import dynamic from 'next/dynamic';
import { useInView } from '@blaze-react/utils/lib/customHooks';
import { LoadingComponent } from '@blaze-cms/react-page-builder';
import { CHART_CONTAINER_IN_VIEW_CONFIG } from './constants';

const Chart = dynamic(() => import(/* webpackChunkName: "blazePbChartComponent" */ './Chart'));

const ChartContainer = ({
  chartFileUrl,
  chartType,
  chartTitle,
  showLegend,
  axesOptions,
  datasetsOptions
}) => {
  const [isIntersecting, containerRef] = useInView(CHART_CONTAINER_IN_VIEW_CONFIG);
  const [chartData, setChartData] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchChartData = () => {
      if (!chartFileUrl || !isIntersecting) return;
      fetch(chartFileUrl)
        .then(res => res.text())
        .then(res => {
          setChartData(res);
          setIsLoading(false);
        })
        .catch(err => {
          setIsLoading(false);
          setError(err.message);
        });
    };
    fetchChartData();
  }, [chartFileUrl, isIntersecting]);

  if (error) {
    return <div data-testid="chart-error">{error}</div>;
  }

  const chartOptions = {
    chartType,
    chartTitle,
    showLegend,
    axesOptions,
    datasetsOptions
  };

  return (
    <div ref={containerRef} data-testid="chart-container" className="chart-container">
      {isLoading ? (
        <LoadingComponent />
      ) : (
        <Chart chartData={chartData} chartOptions={chartOptions} />
      )}
    </div>
  );
};

ChartContainer.propTypes = {
  chartFileUrl: PropTypes.string,
  chartType: PropTypes.string,
  chartTitle: PropTypes.string,
  showLegend: PropTypes.bool,
  axesOptions: PropTypes.array,
  datasetsOptions: PropTypes.array
};

ChartContainer.defaultProps = {
  chartFileUrl: null,
  chartType: 'bar',
  chartTitle: '',
  showLegend: true,
  axesOptions: [],
  datasetsOptions: []
};

export default ChartContainer;
